import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Save, X, CheckCircle2 } from "lucide-react";
import { ConclusaoAutomatica, PerguntaFormulario } from '@/types/tarefas';

interface ConclusoesAutomaticasEditorProps {
  conclusoes: ConclusaoAutomatica[];
  perguntasFim: PerguntaFormulario[]; // Apenas perguntas marcadas com finaliza_fluxo
  todasPerguntas: PerguntaFormulario[];
  getPerguntaLabel: (perguntaId: string, perguntasBase?: PerguntaFormulario[]) => string;
  onChange: (conclusoes: ConclusaoAutomatica[]) => void;
}

export function ConclusoesAutomaticasEditor({
  conclusoes,
  perguntasFim,
  todasPerguntas,
  getPerguntaLabel,
  onChange
}: ConclusoesAutomaticasEditorProps) {
  const [editingConclusao, setEditingConclusao] = useState<ConclusaoAutomatica | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [nomeConclusao, setNomeConclusao] = useState('');
  const [perguntasSelecionadas, setPerguntasSelecionadas] = useState<Set<string>>(new Set());

  const handleAddConclusao = () => {
    setEditingConclusao(null);
    setNomeConclusao('');
    setPerguntasSelecionadas(new Set());
    setShowDialog(true);
  };

  const handleEditConclusao = (conclusao: ConclusaoAutomatica) => {
    setEditingConclusao(conclusao);
    setNomeConclusao(conclusao.nome);
    setPerguntasSelecionadas(new Set(conclusao.pergunta_ids));
    setShowDialog(true);
  };

  const handleSaveConclusao = () => {
    if (!nomeConclusao.trim()) {
      return;
    }

    if (perguntasSelecionadas.size === 0) {
      return;
    }

    const novaConclusao: ConclusaoAutomatica = {
      id: editingConclusao?.id || `conclusao-${Date.now()}-${Math.random()}`,
      nome: nomeConclusao.trim(),
      pergunta_ids: Array.from(perguntasSelecionadas)
    };

    if (editingConclusao) {
      // Editar conclusão existente
      const conclusoesAtualizadas = conclusoes.map(c =>
        c.id === editingConclusao.id ? novaConclusao : c
      );
      onChange(conclusoesAtualizadas);
    } else {
      // Adicionar nova conclusão
      onChange([...conclusoes, novaConclusao]);
    }

    setShowDialog(false);
    setEditingConclusao(null);
    setNomeConclusao('');
    setPerguntasSelecionadas(new Set());
  };

  const handleDeleteConclusao = (conclusaoId: string) => {
    if (confirm('Tem certeza que deseja excluir esta conclusão automática?')) {
      onChange(conclusoes.filter(c => c.id !== conclusaoId));
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setEditingConclusao(null);
    setNomeConclusao('');
    setPerguntasSelecionadas(new Set());
  };

  const togglePergunta = (perguntaId: string) => {
    const novasSelecionadas = new Set(perguntasSelecionadas);
    if (novasSelecionadas.has(perguntaId)) {
      novasSelecionadas.delete(perguntaId);
    } else {
      novasSelecionadas.add(perguntaId);
    }
    setPerguntasSelecionadas(novasSelecionadas);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Conclusões Automáticas</CardTitle>
              <CardDescription>
                Defina conclusões baseadas em quais perguntas de fim foram respondidas
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddConclusao}
              disabled={perguntasFim.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Conclusão
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {perguntasFim.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">
              <p>Nenhuma pergunta de fim disponível</p>
              <p className="text-xs mt-1">Marque algumas perguntas como "Fim" para criar conclusões automáticas</p>
            </div>
          ) : (
            <>
              {conclusoes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg">
                  <p>Nenhuma conclusão automática criada</p>
                  <p className="text-xs mt-1">Clique em "Adicionar Conclusão" para começar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conclusoes.map((conclusao) => {
                    const perguntasConclusao = conclusao.pergunta_ids
                      .map(id => perguntasFim.find(p => p.id === id))
                      .filter(Boolean) as PerguntaFormulario[];

                    return (
                      <Card key={conclusao.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-sm font-semibold">
                                {conclusao.nome}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {conclusao.pergunta_ids.length} pergunta(s)
                              </span>
                            </div>
                            <div className="ml-2 mt-2">
                              <p className="text-xs text-muted-foreground mb-1">Perguntas que levam a esta conclusão:</p>
                              <div className="flex flex-wrap gap-2">
                                {perguntasConclusao.map((pergunta) => (
                                  <Badge
                                    key={pergunta.id}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {getPerguntaLabel(pergunta.id, todasPerguntas)}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditConclusao(conclusao)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteConclusao(conclusao.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog para adicionar/editar conclusão */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {editingConclusao ? 'Editar Conclusão Automática' : 'Nova Conclusão Automática'}
            </DialogTitle>
            <DialogDescription>
              Selecione quais perguntas de fim, quando respondidas, levam a esta conclusão
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nome-conclusao">Nome da Conclusão *</Label>
              <Input
                id="nome-conclusao"
                value={nomeConclusao}
                onChange={(e) => setNomeConclusao(e.target.value)}
                placeholder="Ex: Cliente Quente, Cliente Frio, etc."
              />
            </div>

            <div>
              <Label>Selecione as Perguntas de Fim *</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Marque as perguntas que, quando respondidas, levam a esta conclusão
              </p>
              <ScrollArea className="h-[300px] border rounded-md p-4">
                <div className="space-y-3">
                  {perguntasFim.map((pergunta) => {
                    const isSelected = perguntasSelecionadas.has(pergunta.id);
                    return (
                      <div
                        key={pergunta.id}
                        className="flex items-start space-x-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                        onClick={() => togglePergunta(pergunta.id)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => togglePergunta(pergunta.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`pergunta-${pergunta.id}`}
                            className="cursor-pointer font-normal"
                          >
                            {getPerguntaLabel(pergunta.id, todasPerguntas)}
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {pergunta.texto}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              {perguntasSelecionadas.size > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {perguntasSelecionadas.size} pergunta(s) selecionada(s)
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleSaveConclusao}
              disabled={!nomeConclusao.trim() || perguntasSelecionadas.size === 0}
            >
              <Save className="h-4 w-4 mr-2" />
              {editingConclusao ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

